import { castName } from "../art/cast";
import { $, h } from "../lib/dom";
import { isEn, locale, t, tr } from "../i18n";
import { toTrad } from "../lib/s2t";
import {
  type CallMeta,
  type ModelTier,
  SAMPLE_ENDPOINT,
  type SampleErrorCode,
  type SampleResponse,
} from "../../shared/llm-api";
import { currentDialog, updateControls } from "./dialog";
import { post } from "./log";
import { dropSession, ensureSession } from "./session";
import { L, rt, S, sessionGuard } from "./state";
import { renderHUD, spend } from "./time";
import { onTalked } from "./waits";
import { renderZBar } from "./zoom";

export class LlmError extends Error {
  constructor(readonly code: SampleErrorCode | "network") {
    super(code);
    this.name = "LlmError";
  }
}

async function requestSample(prompt: string, tier: ModelTier, meta: CallMeta): Promise<SampleResponse> {
  let res: Response;
  try {
    res = await fetch(SAMPLE_ENDPOINT, {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ prompt, tier, meta, locale }),
    });
  } catch {
    throw new LlmError("network");
  }
  try {
    return (await res.json()) as SampleResponse;
  } catch {
    throw new LlmError("upstream_error");
  }
}

/**
 * Ask the Worker proxy for one JSON object. Establishes a player session
 * first, and once more if the server reports it expired.
 */
export async function askJSON<T = Record<string, unknown>>(
  prompt: string,
  tier: ModelTier,
  meta: CallMeta = { kind: tier === "default" ? "debrief" : "other" },
): Promise<T> {
  const tagged: CallMeta = { ...meta, level: meta.level ?? (S.live ? L.id : undefined) };
  let body: SampleResponse | null = null;
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      await ensureSession();
    } catch {
      throw new LlmError("no_session");
    }
    body = await requestSample(prompt, tier, tagged);
    if (body.ok || body.code !== "no_session") break;
    dropSession();
  }
  if (!body?.ok) throw new LlmError(body?.code ?? "no_session");
  if (typeof body.data !== "object" || body.data === null) throw new LlmError("invalid_json");
  return body.data as T;
}

export function setBanner(text: string): void {
  const b = $("#banner");
  b.textContent = text;
  b.classList.add("show");
}

function handleErr(e: unknown, ch: string): void {
  const code = e instanceof LlmError ? e.code : "network";
  if (code === "not_configured") {
    S.llmOff = true;
    setBanner(t("err.noKey"));
  } else if (code === "quota_exhausted") post(ch, "narr", null, t("err.quota"));
  else if (code === "no_session") post(ch, "narr", null, t("err.noSession"));
  else if (code === "rate_limited") post(ch, "narr", null, t("err.rate"));
  else post(ch, "narr", null, t("err.network"));
}

export function setBusy(b: boolean): void {
  S.busy = b;
  if (b) rt.idle = 0;
  updateControls();
  renderZBar();
}

let thinking: HTMLElement | null = null;

function showThinking(who: string): void {
  hideThinking();
  thinking = h(
    "div",
    { className: "msg npc thinking" },
    h("div", { className: "who", text: castName(who) }),
    h("div", { className: "txt", text: t("side.thinking") }),
  );
  const log = $("#log");
  log.appendChild(thinking);
  log.scrollTop = log.scrollHeight;
}

export function hideThinking(): void {
  thinking?.remove();
  thinking = null;
}

/** Model output in the player's edition: zh replies get the simplified→traditional safety net. */
export const localText = (s: string): string => (isEn ? s : toTrad(s));

const TRANSCRIPT = tr(
  { me: "Clerk：", narr: "（旁白）", result: "（結果）", said: "：", none: "（尚無）" },
  { me: "Clerk: ", narr: "(Narration) ", result: "(Result) ", said: ": ", none: "(nothing yet)" },
);

function transcript(ch: string, n = 30): string {
  const lb = TRANSCRIPT;
  return (
    S.log
      .filter((e) => e.ch === ch)
      .slice(-n)
      .map((e) =>
        e.kind === "me"
          ? `${lb.me}${e.text}`
          : e.kind === "narr"
            ? `${lb.narr}${e.text}`
            : e.kind === "result"
              ? `${lb.result}${e.text}`
              : `${castName(e.who)}${lb.said}${e.text}`,
      )
      .join("\n") || lb.none
  );
}

interface PromptParts {
  body: string;
  mood: string;
  keys: string[];
  who: string;
  multi: boolean;
  extra: string | undefined;
  log: string;
  latest: string;
}

function chatPrompt(p: PromptParts): string {
  return tr(
    `${p.body}
${p.mood ? "\n【情緒】\n" + p.mood : ""}
【輸出格式】只輸出 JSON，不要其他文字：
{"lines":[{"who":"${p.keys[0] ?? ""}","text":"..."}]${p.extra ? "," + p.extra : ""}}
who 只能是：${p.who}。一律使用台灣繁體中文與台灣用語，絕對不要出現任何簡體字。lines 依說話順序排列。台灣口語繁體中文，每人每次 1 到 3 句，只回應 clerk 這一次的話。${p.multi ? "多數時候只有一個人回應；其他人只有在話題跟自己的職責或處境直接相關、真實場景裡會自然插話時才開口，不要每個人都講。" : ""}

【對話紀錄】
${p.log}

${p.latest}`,
    `${p.body}
${p.mood ? "\n[MOOD]\n" + p.mood : ""}
[OUTPUT FORMAT] Output JSON only, nothing else:
{"lines":[{"who":"${p.keys[0] ?? ""}","text":"..."}]${p.extra ? "," + p.extra : ""}}
"who" must be one of: ${p.who}. Write natural, spoken American English; never use Chinese characters. List lines in the order they are spoken. Each person says 1 to 3 sentences per turn and responds only to what the clerk just said.${p.multi ? " Most of the time only one person answers; someone else speaks up only when the topic bears directly on their job or situation and they would naturally chime in, never everyone each time." : ""}

[CONVERSATION SO FAR]
${p.log}

${p.latest}`,
  );
}

/** Shape every in-character reply shares; `extra` fields are level-specific. */
export interface ChatReply {
  lines?: { who?: string; text?: string }[];
  [extra: string]: unknown;
}

export interface ChatCfg {
  /** Log channel. */
  ch: string;
  /** Who shows the "…" bubble while waiting. */
  think: string;
  /** cast key → role description; the model may answer as any of them. */
  speakers: Record<string, string>;
  /** Character sheet / scene prompt. */
  body: string;
  /** Extra JSON fields (raw JSON fragment) the model should fill in. */
  extra?: string;
  tier?: ModelTier;
  /** Game minutes this exchange costs (default 2; 0 = free). */
  cost?: number;
  after?(r: ChatReply): void;
}

/** One model call; any of `cfg.speakers` may answer, in order. */
export async function chat(text: string, cfg: ChatCfg, latest?: string): Promise<ChatReply | null> {
  setBusy(true);
  showThinking(cfg.think);
  S.speaking = cfg.think;
  const keys = Object.keys(cfg.speakers);
  const who = Object.entries(cfg.speakers)
    .map(([k, v]) => tr(`${k}（${v}）`, `${k} (${v})`))
    .join(tr("、", ", "));
  const mood = keys
    .filter((k) => S.mood[k])
    .map((k) => {
      const n = Math.min(3, S.mood[k] ?? 0);
      return tr(
        `${castName(k)}目前對 clerk 的不耐煩程度 ${n}/3，語氣要反映出來。`,
        `${castName(k)}'s impatience with the clerk is at ${n}/3; let it show in their tone.`,
      );
    })
    .join("\n");
  const parts: PromptParts = {
    body: cfg.body,
    mood,
    keys,
    who,
    multi: keys.length > 1,
    extra: cfg.extra,
    log: transcript(cfg.ch),
    latest: latest ?? tr(`Clerk 剛剛說：「${text}」`, `The clerk just said: "${text}"`),
  };
  const prompt = chatPrompt(parts);
  const alive = sessionGuard();
  let r: ChatReply | null;
  try {
    r = await askJSON<ChatReply>(prompt, cfg.tier ?? "quick", { kind: "chat", ch: cfg.ch, said: text });
  } catch (e) {
    if (!alive()) return null;
    hideThinking();
    handleErr(e, cfg.ch);
    r = null;
  }
  if (!alive()) return null;
  if (r) {
    hideThinking();
    for (const ln of r.lines ?? [])
      if (ln.text && ln.who && cfg.speakers[ln.who]) post(cfg.ch, "npc", ln.who, localText(ln.text));
  }
  S.speaking = null;
  setBusy(false);
  if (r) cfg.after?.(r);
  if (cfg.cost !== 0) spend(cfg.cost ?? 2, t("time.talk"));
  updateControls();
  renderHUD();
  return r;
}

/** Narrow a model-provided list field to strings. */
export const strList = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];

/** Composer submit: post the clerk's line and hand it to the dialog. */
export async function sendText(): Promise<void> {
  const inp = $<HTMLTextAreaElement>("#input");
  const text = inp.value.trim();
  if (!text || !S.live || S.busy || S.ended || S.llmOff || !S.dialog || S.cine) return;
  const id = S.dialog;
  const D = currentDialog();
  if (!D?.onTalk) return;
  inp.value = "";
  rt.fastForward = false;
  post(D.ch, "me", null, text);
  onTalked(id);
  await D.onTalk(text);
}
